import {
  Statistics,
  StatisticsStatus,
} from "@app/core/statistics/domain/model/statistics";
import { Section } from "@app/core/match/domain/model/section";

const statusMapper = {
  "In progress": StatisticsStatus.IN_PROGRESS,
  OK: StatisticsStatus.OK,
};

export class StatisticsMapper {

  private static orderApiResponse(response: any[]): any[] {
    const orderedResponse = response
      .map((group) => ({
        ...group,
        statistics: group.statistics
          .map((stat: any) => ({
            ...stat,
            stat: Array.isArray(stat.stat)
              ? stat.stat.sort((a: any, b: any) => a.value_orden - b.value_orden)
              : stat.stat,
          }))
        .sort((a: any, b: any) => a.orden - b.orden),
      }))
      .sort((a: any, b: any) => a.order - b.order);

    return orderedResponse
  }

  private static processMatchStatistics(statistics: any[], overview: any) {
    for (const stat of statistics) {
      let units = stat.stat.units
      if(stat.id==='golpes_punto') units = 'strokes'
      overview.simple_data?.push({
        name: stat.title,
        info: stat.description,
        units: units,
        value: stat.stat.value,
        cols: 12,
        xl_cols: 3,
        xl_display: 'col',
        display: 'row',
        bold: true
      });
    }
  }

  private static initializeTabs(tabs: any, id: string) {
    if (!tabs) {
      return {
        section_id: 'performance-detailed',
        section_title: 'stats.performance-detailed',
        stat_type: 'tabs',
        tabs: [{
          id: id,
          title: id,
          stats: []
        }]
      };
    } else {
      tabs.tabs?.push({
        id: id,
        title: id,
        stats: []
      });
      return tabs;
    }
  }

  private static processVerticalStatistics(stat: any, tabs: any, tabIdx: number, positionIndexes: any) {
    if (stat.id === "pct_primeros_saques") {
      tabs.tabs![tabIdx].stats?.push({
        section_id: stat.id,
        section_info: stat.description,
        stat_type: 'simple-data',
        simple_data: [{ ...stat.stat, cols: 6, truncate: true }],
      });
      positionIndexes.servesSimpleIndex = tabs.tabs![tabIdx].stats!.length - 1;
    } else {
      tabs.tabs![tabIdx].stats?.push({
        section_id: stat.id,
        section_title: stat.title,
        section_info: stat.description,
        stat_type: 'boxdata',
        boxdata: [{ ...stat.stat, progressbar: true }],
      });
    }

    if (stat.id === "basculacio_vertical_individual") positionIndexes.positionIndividualIndex = tabs.tabs![tabIdx].stats!.length - 1;
    else if (stat.id === "pct_basc_vertical") positionIndexes.positionTeamIndex = tabs.tabs![tabIdx].stats!.length - 1;
    else if (stat.id === "pct_basc_vertical_rival") positionIndexes.positionOponentIndex = tabs.tabs![tabIdx].stats!.length - 1;
  }

  private static processHorizontalStatistics(stat: any, tabs: any, tabIdx: number, positionIndexes: any) {
    if (stat.id === "basculacio_horitzontal_individual") {
      tabs.tabs![tabIdx].stats![positionIndexes.positionIndividualIndex!].boxdata!.push({ ...stat.stat, progressbar: true });
    } else if (stat.id === "pct_basc_hor") {
      tabs.tabs![tabIdx].stats![positionIndexes.positionTeamIndex!].boxdata!.push({ ...stat.stat, progressbar: true });
    } else if (stat.id === "pct_basc_hor_rival") {
      tabs.tabs![tabIdx].stats![positionIndexes.positionOponentIndex!].boxdata!.push({ ...stat.stat, progressbar: true });
    } else if (stat.id === "velocidadsaques") {
      tabs.tabs![tabIdx].stats![positionIndexes.servesSimpleIndex!].simple_data!.push({ ...stat.stat, name: stat.title, cols: 6 });
    }
  }

  private static processOtherStatistics(stat: any, tabs: any, tabIdx: number) {
    switch (stat.id) {
      case "velo_hits":
      case "golpes":
      case "golpes_equipo_rival":
        tabs.tabs![tabIdx].stats!.push({
          section_id: stat.id,
          section_title: stat.title,
          section_info: stat.description,
          stat_type: 'boxdata',
          boxdata: stat.stat,
        });
        break;
      case "restos":
        tabs.tabs![tabIdx].stats!.push({
          section_id: stat.id,
          section_title: stat.title,
          section_info: stat.description,
          stat_type: 'double-progressbar',
          double_progressbar: stat.stat,
        });
        break;
      default:
        if (stat.type === "piechart") {
          tabs.tabs![tabIdx].stats!.push({
            section_id: stat.id,
            section_title: stat.title,
            section_info: stat.description,
            stat_type: 'piechart',
            piechart: stat.stat,
          });
        } else if (stat.type === "progressbar") {
          tabs.tabs![tabIdx].stats!.push({
            section_id: stat.id,
            section_title: stat.title,
            section_info: stat.description,
            stat_type: 'progressbar',
            progressbar: stat.stat,
          });
        }
        break;
    }
  }

  private static mapSections(response: any[], overview: Section): Section[] {

    let sections: Section[] = [];
    let tabs: Section | undefined = undefined;

    let tabIdx = 0;
    let positionIndexes = {
      positionIndividualIndex: null,
      positionTeamIndex: null,
      positionOponentIndex: null,
      servesSimpleIndex: null
    };
  
    for (const key in response) {
      const section = response[key];

      switch (section.id) {
        case 'match':
          this.processMatchStatistics(section.statistics, overview);
          break;
        case 'position':
        case 'strokes':
        case 'serves':
          tabs = this.initializeTabs(tabs, section.id);
          for (const stat of section.statistics) {
            switch (stat.id) {
              case "basculacio_vertical_individual":
              case "pct_basc_vertical":
              case "pct_basc_vertical_rival":
              case "pct_primeros_saques":
                this.processVerticalStatistics(stat, tabs, tabIdx, positionIndexes);
                break;
              case "basculacio_horitzontal_individual":
              case "pct_basc_hor":
              case "pct_basc_hor_rival":
              case "velocidadsaques":
                this.processHorizontalStatistics(stat, tabs, tabIdx, positionIndexes);
                break;
              default:
                this.processOtherStatistics(stat, tabs, tabIdx);
                break;
            }
          }
          tabIdx += 1;
          break;
      }

    }
    if (overview) sections.push(overview);
    if (tabs) sections.push(tabs);
    return sections

  }

  static deserialize(response: any): Statistics {

    if (response.all_stats.length === 0) {
      return new Statistics({
        status: statusMapper[response.status as keyof typeof statusMapper],
        stats: [],
      });
    }

    let sections: Section[] = []
    const orderedResponse = this.orderApiResponse(response.all_stats);

    let overview: Section = {
      section_id: 'overview',
      section_title: 'statistics.overview',
      stat_type: 'simple-data',
      simple_data: [
        {
          name: 'statistics.total_matches',
          value: response.total_matches,
          cols: 6,
          xl_cols: 3,
          bold: true,
          truncate: true
        },
        {
          name: 'statistics.wins',
          units: 'percentage',
          value: Math.trunc(response.avg_victories*100),
          cols: 6,
          xl_cols: 3,
          bold: true,
          truncate: false,
        }
      ]
    }

    sections = this.mapSections(orderedResponse, overview);

    return new Statistics({
      status: statusMapper[response.status as keyof typeof statusMapper],
      stats: sections,
      totalMatches: response.total_matches,
    });
  }
}
